import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CampaignCard, CampaignCardSkeleton } from "@/components/public/CampaignCard";
import { BannerAd, insertBanners } from "@/components/public/BannerAd";
import { BrandMark } from "@/components/BrandMark";
import { CitySelect } from "@/components/CitySelect";
import { BUSINESS_TYPES } from "@/lib/businessTypes";
import { MapPin, Loader2, Search, Trophy, LogIn, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

/**
 * Explore / Near You — landing feed of every publicly-listed campaign
 * across all three game types.
 *
 * Visual language matches the homepage exactly (cream/ink/gold + Anton
 * headline + unified CampaignCard) so users moving between `/` and
 * `/explore` see zero style drift.
 *
 * Two tabs:
 *   - Near Me   — requests browser geolocation; distance-sorted results.
 *   - Browse All — no location required; recency-sorted.
 *
 * Filters (city, category) apply to both tabs and hit the server via
 * `GET /public/campaigns`. Category selector is bound to the canonical
 * BUSINESS_TYPES taxonomy — never invent categories client-side.
 */
const ANTON = '"Anton", "Impact", sans-serif';

export default function ExplorePage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("browse");
  const [coords, setCoords] = useState(null);
  const [locationBusy, setLocationBusy] = useState(false);
  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const isLoggedIn = Boolean(
    typeof window !== "undefined" && localStorage.getItem("citydooh_customer_token"),
  );

  const requestLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Location isn't available in this browser");
      return;
    }
    setLocationBusy(true);
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setCoords({ lat: p.coords.latitude, lng: p.coords.longitude });
        setLocationBusy(false);
      },
      () => {
        toast.error("Location permission denied");
        setLocationBusy(false);
        setTab("browse");
      },
      { enableHighAccuracy: false, timeout: 8000 },
    );
  };

  useEffect(() => {
    if (tab === "near" && !coords) requestLocation();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setItems(null);
    setError(null);
    const params = {};
    if (city) params.city = city;
    if (category) params.category = category;
    if (tab === "near" && coords) {
      params.lat = coords.lat;
      params.lng = coords.lng;
      params.radius_km = 50;
    }
    api.publicCampaigns(params)
      .then((r) => setItems(r.data || []))
      .catch(() => setError("Couldn't load campaigns"));
  }, [tab, coords, city, category]);

  // Only surface categories that actually have live listings — but
  // always in canonical order so the dropdown never reshuffles.
  const availableCategories = useMemo(() => {
    if (!items) return BUSINESS_TYPES;
    const present = new Set(items.map((i) => i.vendor?.business_type).filter(Boolean));
    return BUSINESS_TYPES.filter((c) => present.has(c));
  }, [items]);

  return (
    <div className="min-h-screen bg-[#F7F4EC] text-[#0A0A0A]" data-testid="explore-root">
      {/* Slim header shared style with homepage/login/winnings */}
      <header className="sticky top-0 z-30 border-b-2 border-[#0A0A0A] bg-[#F7F4EC]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-20 md:h-24 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0A0A0A] rounded-md" aria-label="City DOOH home">
            <BrandMark size="header" data-testid="explore-brand" />
          </Link>
          <Link
            to="/"
            className="hidden md:inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest hover:text-[#F5A800]"
            data-testid="explore-home-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Home
          </Link>
          <div className="flex items-center gap-2">
            {isLoggedIn ? (
              <Button
                variant="outline"
                className="h-11 md:h-9 border-2 border-[#0A0A0A] text-[#0A0A0A] font-bold uppercase tracking-widest text-xs hover:bg-[#F5A800] hover:border-[#0A0A0A]"
                onClick={() => navigate("/customer/my-winnings")}
                data-testid="explore-my-winnings"
              >
                <Trophy className="h-3.5 w-3.5 mr-1.5" /> My Winnings
              </Button>
            ) : (
              <Button
                variant="outline"
                className="h-11 md:h-9 border-2 border-[#0A0A0A] text-[#0A0A0A] font-bold uppercase tracking-widest text-xs hover:bg-[#F5A800] hover:border-[#0A0A0A]"
                onClick={() => navigate("/customer/login")}
                data-testid="explore-signin"
              >
                <LogIn className="h-3.5 w-3.5 mr-1.5" /> Sign in
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-10">
        <div className="mb-6">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.25em] px-3 py-1.5 rounded-full bg-[#0A0A0A] text-[#F5A800] border-2 border-[#0A0A0A]"
          >
            Explore CityDooh
          </span>
          <h1
            className="mt-4 text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9] tracking-tight"
            style={{ fontFamily: ANTON }}
            data-testid="explore-title"
          >
            EVERY LIVE OFFER,<br />
            <span className="text-[#F5A800]">ONE FEED.</span>
          </h1>
          <p className="mt-3 text-sm md:text-base text-[#0A0A0A]/70 max-w-2xl">
            Spin wheels, scratch cards and instant offers from local businesses. Filter by city or
            category, or turn on location for the nearest rewards first.
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} className="mb-4">
          <TabsList
            className="bg-white border-2 border-[#0A0A0A] p-1 h-11"
            style={{ boxShadow: "3px 3px 0 #0A0A0A" }}
          >
            <TabsTrigger
              value="near"
              className="h-9 px-4 text-xs font-bold uppercase tracking-widest data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-[#F5A800]"
              data-testid="explore-tab-near"
            >
              <MapPin className="h-3.5 w-3.5 mr-1.5" /> Near Me
            </TabsTrigger>
            <TabsTrigger
              value="browse"
              className="h-9 px-4 text-xs font-bold uppercase tracking-widest data-[state=active]:bg-[#0A0A0A] data-[state=active]:text-[#F5A800]"
              data-testid="explore-tab-browse"
            >
              Browse All
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Filter bar */}
        <div
          className="bg-white border-2 border-[#0A0A0A] rounded-2xl p-3 flex flex-col md:flex-row md:items-center gap-2 mb-6"
          style={{ boxShadow: "4px 4px 0 #0A0A0A" }}
        >
          <div className="flex-1 flex items-center gap-2 px-2">
            <CitySelect
              value={city}
              onChange={setCity}
              placeholder="Filter by city"
              triggerClassName="h-10 border-0 shadow-none justify-start"
              testIdPrefix="explore-city"
            />
          </div>
          <div className="hidden md:block w-px self-stretch bg-[#0A0A0A]/20" />
          <div className="flex-1 flex items-center gap-2 px-2 border-t md:border-t-0 border-[#0A0A0A]/10 pt-2 md:pt-0">
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="h-10 w-full rounded-md border-0 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A800]"
              data-testid="explore-category-select"
            >
              <option value="">All categories</option>
              {availableCategories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          {tab === "near" && (
            <Button
              variant="outline"
              className="h-10 border-2 border-[#0A0A0A] font-bold uppercase tracking-widest text-xs hover:bg-[#F5A800]"
              onClick={requestLocation}
              disabled={locationBusy}
            >
              {locationBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <>
                  <Search className="h-3.5 w-3.5 mr-1" /> Refresh location
                </>
              )}
            </Button>
          )}
        </div>

        {/* Active category chip */}
        {category && (
          <div className="mb-4 flex items-center gap-3 text-sm">
            <Badge className="bg-[#F5A800] text-[#0A0A0A] hover:bg-[#F5A800] border-2 border-[#0A0A0A]">
              {category}
            </Badge>
            <button
              onClick={() => setCategory("")}
              className="underline text-[#0A0A0A]/60 hover:text-[#0A0A0A]"
            >
              Clear
            </button>
          </div>
        )}

        {/* Feed */}
        {error && (
          <div
            className="rounded-2xl border-2 border-[#0A0A0A] bg-white p-8 text-center"
            style={{ boxShadow: "4px 4px 0 #0A0A0A" }}
          >
            <p className="font-bold text-[#0A0A0A]" style={{ fontFamily: ANTON }}>{error}</p>
          </div>
        )}
        {!error && items === null && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => <CampaignCardSkeleton key={i} />)}
          </div>
        )}
        {!error && items && items.length === 0 && (
          <div
            className="rounded-2xl border-2 border-dashed border-[#0A0A0A]/40 bg-white p-12 text-center"
            data-testid="explore-empty"
          >
            <p className="text-xl uppercase" style={{ fontFamily: ANTON }}>Nothing here yet</p>
            <p className="text-sm text-[#0A0A0A]/60 mt-1">Adjust your filters or check back soon.</p>
          </div>
        )}
        {!error && items && items.length > 0 && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6"
            data-testid="explore-grid"
          >
            {insertBanners(items, "between_offers", { every: 6, minReal: 6 }).map((item) =>
              item.kind === "card"
                ? <CampaignCard key={item.key} campaign={item.data} />
                : <BannerAd key={item.key} zone={item.zone} compact className="rounded-2xl" />
            )}
          </div>
        )}

        <p className="text-xs text-[#0A0A0A]/50 text-center mt-10">
          Powered by CityDooh —{" "}
          <Link to="/" className="underline hover:text-[#0A0A0A]">Home</Link>
        </p>
      </main>
    </div>
  );
}
